import React, { useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

/**
 * AppModal — the single modal shell used across the system.
 *
 * Fixes the problems of the hand-rolled `<div className="modal show">` blocks
 * it replaces: those could only be dismissed via their own close button, they
 * rendered inside the page tree (so transformed/overflow-hidden ancestors could
 * clip them), long forms overflowed the viewport with no way to scroll, and the
 * page behind them kept scrolling.
 *
 * Behaviour:
 *   • Dismiss with the close button, the Cancel/footer buttons, the Esc key or
 *     a click on the backdrop. Only the top-most modal reacts to Esc/backdrop.
 *   • Rendered in a portal on <body>, so no ancestor can clip or restack it.
 *   • Body scroll is locked while open (reference-counted for stacked modals)
 *     and the scrollbar width is compensated so the page doesn't jump.
 *   • The body scrolls internally; header and footer stay pinned.
 *   • Focus moves into the dialog on open, is trapped inside while open, and is
 *     restored to the trigger on close.
 *
 * Props:
 *   show            render or not (default true — callers usually guard with &&)
 *   onClose         () => void, called for every dismissal route
 *   title           string or node shown in the header
 *   subtitle        optional muted line under the title
 *   icon            optional Font Awesome class, e.g. "fa-solid fa-pen"
 *   size            "sm" | "md" | "lg" | "xl" | "full"   (default "md")
 *   footer          node rendered inside .modal-footer (omit for no footer)
 *   busy            when true, Esc/backdrop dismissal is blocked (save in flight)
 *   closeOnBackdrop / closeOnEsc  opt out of those dismissal routes
 *   bodyClassName   extra classes on .modal-body
 */

const SIZE_CLASS = {
  sm: "modal-sm",
  md: "",
  lg: "modal-lg",
  xl: "modal-xl",
  full: "modal-xl modal-fullscreen-lg-down",
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Shared across every instance so stacked modals lock/unlock the body once.
let openModalCount = 0;
const openStack = [];

const lockBodyScroll = () => {
  if (openModalCount === 0) {
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.dataset.appmodalPadding = document.body.style.paddingRight || "";
    document.body.dataset.appmodalOverflow = document.body.style.overflow || "";
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    document.body.style.overflow = "hidden";
  }
  openModalCount += 1;
};

const unlockBodyScroll = () => {
  openModalCount = Math.max(0, openModalCount - 1);
  if (openModalCount === 0) {
    document.body.style.paddingRight = document.body.dataset.appmodalPadding || "";
    document.body.style.overflow = document.body.dataset.appmodalOverflow || "";
    delete document.body.dataset.appmodalPadding;
    delete document.body.dataset.appmodalOverflow;
  }
};

const AppModal = ({
  show = true,
  onClose,
  title,
  subtitle,
  icon,
  size = "md",
  footer,
  onSubmit,
  busy = false,
  closeOnBackdrop = true,
  closeOnEsc = true,
  bodyClassName = "",
  children,
}) => {
  const dialogRef = useRef(null);
  const backdropRef = useRef(null);
  // Tracks whether the pointer press that opened this click began on the
  // backdrop. Without it, releasing a text selection outside the dialog would
  // close the modal and throw away the user's input.
  const pressedOnBackdrop = useRef(false);
  const restoreFocusRef = useRef(null);
  const idRef = useRef(`appmodal-${Math.random().toString(36).slice(2, 9)}`);

  const requestClose = useCallback(() => {
    if (busy) return;
    if (typeof onClose === "function") onClose();
  }, [busy, onClose]);

  // ── Scroll lock + modal stack registration ───────────────────────────────
  useEffect(() => {
    if (!show) return undefined;
    const token = idRef.current;
    lockBodyScroll();
    openStack.push(token);
    return () => {
      const at = openStack.indexOf(token);
      if (at !== -1) openStack.splice(at, 1);
      unlockBodyScroll();
    };
  }, [show]);

  // ── Esc to dismiss + Tab focus trap (top-most modal only) ────────────────
  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e) => {
      if (openStack[openStack.length - 1] !== idRef.current) return;
      if (e.key === "Escape" && closeOnEsc) {
        e.stopPropagation();
        requestClose();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const nodes = Array.from(dialogRef.current.querySelectorAll(FOCUSABLE)).filter(
        (n) => n.offsetParent !== null || n === document.activeElement
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [show, closeOnEsc, requestClose]);

  // ── Move focus in on open, restore it on close ───────────────────────────
  useEffect(() => {
    if (!show) return undefined;
    restoreFocusRef.current = document.activeElement;
    const t = setTimeout(() => {
      if (!dialogRef.current) return;
      const target =
        dialogRef.current.querySelector("[data-autofocus]") ||
        dialogRef.current.querySelector(FOCUSABLE);
      if (target && typeof target.focus === "function") target.focus();
    }, 30);
    return () => {
      clearTimeout(t);
      const prev = restoreFocusRef.current;
      if (prev && typeof prev.focus === "function" && document.contains(prev)) prev.focus();
    };
  }, [show]);

  if (!show) return null;

  const depth = Math.max(0, openStack.indexOf(idRef.current));
  const zIndex = 1090 + depth * 20;

  const onBackdropMouseDown = (e) => {
    pressedOnBackdrop.current = e.target === backdropRef.current;
  };
  const onBackdropClick = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target !== backdropRef.current || !pressedOnBackdrop.current) return;
    pressedOnBackdrop.current = false;
    requestClose();
  };

  const body = (
    <>
      <div className={`modal-body ${bodyClassName}`}>{children}</div>
      {footer !== undefined && footer !== null && footer !== false && (
        <div className="modal-footer">{footer}</div>
      )}
    </>
  );

  return ReactDOM.createPortal(
    <div
      ref={backdropRef}
      className="modal show app-modal d-block"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${idRef.current}-title`}
      onMouseDown={onBackdropMouseDown}
      onClick={onBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        zIndex,
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(2px)",
        overflowY: "auto",
        padding: "1rem 0",
      }}
    >
      <div
        ref={dialogRef}
        className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${SIZE_CLASS[size] ?? ""}`}
      >
        <div className="modal-content app-modal-content">
          <div className="modal-header align-items-start">
            {/* The gap is set here rather than left to `.gap-2`: the template
                ships its own trimmed Bootstrap without the gap utilities, so
                the class alone left every titled modal's icon touching its
                heading. */}
            <div
              className="d-flex align-items-center pe-3"
              style={{ gap: 8 }}
            >
              {icon && <i className={`${icon} tx-16 text-primary`} aria-hidden="true"></i>}
              <div>
                <h6 className="modal-title fw-semibold mb-0" id={`${idRef.current}-title`}>
                  {title}
                </h6>
                {subtitle && <div className="tx-12 tx-muted mt-1">{subtitle}</div>}
              </div>
            </div>
            {/* An explicit glyph, not Bootstrap's background-image `.btn-close`:
                the template's style.css overrides that background away, which
                left every close button in the app an invisible empty box. */}
            <button
              type="button"
              className="app-modal-close"
              aria-label="Close"
              title="Close (Esc)"
              disabled={busy}
              onClick={requestClose}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>

          {onSubmit ? (
            // The form has to be the flex child of .modal-content, otherwise
            // .modal-dialog-scrollable can't constrain .modal-body's height.
            <form
              onSubmit={onSubmit}
              className="app-modal-form"
              style={{ display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}
            >
              {body}
            </form>
          ) : (
            body
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AppModal;
